// ===== 阿里云盘 OpenAPI 客户端 =====
// 需求 9：备份包自动上传到阿里云盘，崩溃 / 换设备后从云盘取回恢复
// 接入方式：阿里云盘开放平台（https://open.alipan.com）注册开发者应用 →
//          获取 client_id / client_secret → 配置回调地址（当前站点域名）→ OAuth 授权
// 流程：OAuth2.0 授权码模式 → 换取 access_token（2h）+ refresh_token →
//       openFile 系列 API 分片上传备份 JSON 到「昌发工作台备份」目录
// 注意：纯前端应用 client_secret 不可避免会暴露，仅用于个人/内部工具备份场景；
//      生产环境建议自建后端代理转发（security 提醒见 SystemPage）

const BASE = 'https://openapi.alipan.com';
const TOKEN_KEY = 'changfa_aliyunpan_token';
const APP_KEY = 'changfa_aliyunpan_app';
const STATE_KEY = 'changfa_aliyunpan_oauth_state';
const VERIFIER_KEY = 'changfa_aliyunpan_oauth_verifier';
export const FOLDER_NAME = '昌发工作台备份';
const KEEP_BACKUPS = 30; // 云盘最多保留最近 30 份
const PART_SIZE = 1024 * 1024; // access_token 2 小时

export interface AliyunpanAppConfig {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
}

export interface AliyunpanToken {
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch ms
  nickName?: string;
  driveId?: string;
}

interface FileEntry {
  file_id: string;
  name: string;
  type: 'folder' | 'file';
  size?: number;
  updated_at?: string;
  created_at?: string;
}

// ===== 配置 / token 持久化 =====
export function saveAppConfig(cfg: AliyunpanAppConfig): void {
  localStorage.setItem(APP_KEY, JSON.stringify(cfg));
}
export function getAppConfig(): AliyunpanAppConfig | null {
  try {
    const raw = localStorage.getItem(APP_KEY);
    return raw ? (JSON.parse(raw) as AliyunpanAppConfig) : null;
  } catch {
    return null;
  }
}
export function clearAppConfig(): void {
  localStorage.removeItem(APP_KEY);
}

export function saveToken(t: AliyunpanToken): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(t));
}
export function getToken(): AliyunpanToken | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as AliyunpanToken) : null;
  } catch {
    return null;
  }
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// ===== OAuth =====

/** 生成并保存一次性 state（防 CSRF：授权回调必须带回同一 state） */
function createOAuthState(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  const state = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  try {
    sessionStorage.setItem(STATE_KEY, state);
  } catch {
    try { localStorage.setItem(STATE_KEY, state); } catch { /* Best-effort operation; failure is non-fatal. */ }
  }
  return state;
}

function readStoredState(): string | null {
  try {
    const s = sessionStorage.getItem(STATE_KEY);
    if (s) { sessionStorage.removeItem(STATE_KEY); return s; }
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  try {
    const s = localStorage.getItem(STATE_KEY);
    if (s) { localStorage.removeItem(STATE_KEY); return s; }
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  return null;
}

// ===== PKCE（S256）=====

/** Uint8Array → base64url（无填充），用于 code_verifier / code_challenge */
function toBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * 生成 PKCE 配对：随机 code_verifier（43-128 字符）+ 其 SHA-256 摘要的 code_challenge（S256）。
 * verifier 只存本机 sessionStorage，绝不随授权 URL 发给阿里云盘，
 * 授权码拦截者没有 verifier 也换不到 token。
 */
async function createPkcePair(): Promise<{ verifier: string; challenge: string }> {
  const verifier = toBase64Url(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  const challenge = toBase64Url(new Uint8Array(digest));
  return { verifier, challenge };
}

function storeVerifier(v: string): void {
  try {
    sessionStorage.setItem(VERIFIER_KEY, v);
  } catch {
    try { localStorage.setItem(VERIFIER_KEY, v); } catch { /* Best-effort operation; failure is non-fatal. */ }
  }
}

function readStoredVerifier(): string | null {
  try {
    const s = sessionStorage.getItem(VERIFIER_KEY);
    if (s) { sessionStorage.removeItem(VERIFIER_KEY); return s; }
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  try {
    const s = localStorage.getItem(VERIFIER_KEY);
    if (s) { localStorage.removeItem(VERIFIER_KEY); return s; }
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  return null;
}

export async function buildAuthUrl(cfg: AliyunpanAppConfig): Promise<string> {
  const { verifier, challenge } = await createPkcePair();
  storeVerifier(verifier);
  const p = new URLSearchParams({
    client_id: cfg.clientId,
    redirect_uri: cfg.redirectUri,
    scope: 'user:base,file:all:write',
    response_type: 'code',
    state: createOAuthState(),
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  return `${BASE}/oauth/authorize?${p.toString()}`;
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { message?: string }).message || `阿里云盘请求失败（${res.status}）`);
  }
  return data as T;
}

interface TokenResp {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user_name?: string;
  nick_name?: string;
}

/** 授权回调 code → 换取 token（登录页跳回 redirect_uri?code=xxx）
 *  PKCE：携带发起授权时保存的 code_verifier 交换；
 *  若应用在开放平台未开启 PKCE 导致交换失败，自动降级为不带 verifier 重试一次。
 */
export async function exchangeCode(cfg: AliyunpanAppConfig, code: string): Promise<AliyunpanToken> {
  const verifier = readStoredVerifier();
  const baseBody: Record<string, unknown> = {
    client_id: cfg.clientId,
    client_secret: cfg.clientSecret,
    grant_type: 'authorization_code',
    code,
    redirect_uri: cfg.redirectUri,
  };
  let data: TokenResp;
  if (verifier) {
    try {
      data = await post<TokenResp>('/oauth/access_token', { ...baseBody, code_verifier: verifier });
    } catch {
      // 降级：开放平台应用配置未启用 PKCE 时会拒绝 code_verifier，去掉重试一次
      console.warn('阿里云盘 PKCE 交换失败，降级为不带 code_verifier 重试');
      data = await post<TokenResp>('/oauth/access_token', baseBody);
    }
  } else {
    data = await post<TokenResp>('/oauth/access_token', baseBody);
  }
  const t: AliyunpanToken = {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + (data.expires_in || 7200) * 1000 - 60_000,
    nickName: data.nick_name || data.user_name,
  };
  saveToken(t);
  return t;
}

async function refreshAccessToken(): Promise<AliyunpanToken | null> {
  const cfg = getAppConfig();
  const t = getToken();
  if (!cfg || !t?.refreshToken) return null;
  try {
    const data = await post<TokenResp>('/oauth/access_token', {
      client_id: cfg.clientId,
      client_secret: cfg.clientSecret,
      grant_type: 'refresh_token',
      refresh_token: t.refreshToken,
    });
    const nt: AliyunpanToken = {
      accessToken: data.access_token,
      refreshToken: data.refresh_token || t.refreshToken,
      expiresAt: Date.now() + (data.expires_in || 7200) * 1000 - 60_000,
      nickName: data.nick_name || t.nickName,
      driveId: t.driveId,
    };
    saveToken(nt);
    return nt;
  } catch {
    return null;
  }
}

/** 获取有效 token（过期自动刷新；刷新失败返回 null 表示需重新授权） */
export async function getValidToken(): Promise<AliyunpanToken | null> {
  const t = getToken();
  if (!t) return null;
  if (t.expiresAt > Date.now()) return t;
  return refreshAccessToken();
}

export async function getUserInfo(token: AliyunpanToken): Promise<{ nick_name: string }> {
  return post<{ nick_name: string }>('/adrive/v1.0/user/getUserInfo', {}, token.accessToken);
}

export async function getDriveInfo(token: AliyunpanToken): Promise<{ default_drive_id: string }> {
  return post<{ default_drive_id: string }>(
    '/adrive/v1.0/user/getDriveInfo',
    {},
    token.accessToken
  );
}

// ===== 文件操作 =====
/** 在 root 下查找指定名称的文件夹，不存在则创建 */
export async function ensureFolder(token: AliyunpanToken, driveId: string): Promise<string> {
  const list = await post<{ items: FileEntry[] }>('/adrive/v1.0/openFile/list', {
    drive_id: driveId,
    parent_file_id: 'root',
    limit: 100,
  }, token.accessToken);
  const folder = list.items?.find((f) => f.type === 'folder' && f.name === FOLDER_NAME);
  if (folder) return folder.file_id;

  const created = await post<FileEntry>('/adrive/v1.0/openFile/create', {
    drive_id: driveId,
    parent_file_id: 'root',
    name: FOLDER_NAME,
    type: 'folder',
    check_name_mode: 'refuse',
  }, token.accessToken);
  return created.file_id;
}

interface CreateFileResp {
  file_id: string;
  upload_id?: string;
  exist?: boolean;
}

/** 上传备份 JSON 文本到云盘备份目录，返回文件名与 file_id */
export async function uploadBackup(
  token: AliyunpanToken,
  driveId: string,
  fileName: string,
  jsonText: string
): Promise<{ fileId: string; name: string; uploaded: boolean }> {
  const folderId = await ensureFolder(token, driveId);
  const size = new TextEncoder().encode(jsonText).byteLength;
  const parts = Math.max(1, Math.ceil(size / PART_SIZE));
  const partInfoList = Array.from({ length: parts }, (_, i) => ({
    part_number: i + 1,
    part_size: Math.min(size - i * PART_SIZE, PART_SIZE),
  }));

  const created = await post<CreateFileResp>('/adrive/v1.0/openFile/create', {
    drive_id: driveId,
    parent_file_id: folderId,
    name: fileName,
    type: 'file',
    check_name_mode: 'auto_rename',
    size,
    part_info_list: partInfoList,
  }, token.accessToken);

  if (created.exist) {
    return { fileId: created.file_id, name: fileName, uploaded: false }; // 秒传
  }
  if (!created.upload_id) throw new Error('创建上传任务失败（未返回 upload_id）');

  const urlResp = await post<{ part_info_list: { part_number: number; upload_url: string }[] }>(
    '/adrive/v1.0/openFile/getUploadUrl',
    {
      drive_id: driveId,
      file_id: created.file_id,
      upload_id: created.upload_id,
      part_info_list: partInfoList,
    },
    token.accessToken
  );

  // 分片上传（逐片 PUT 到预签名 URL）
  const encoder = new TextEncoder();
  const bytes = encoder.encode(jsonText);
  for (const part of urlResp.part_info_list) {
    const start = (part.part_number - 1) * PART_SIZE;
    const chunk = bytes.slice(start, start + PART_SIZE);
    const putRes = await fetch(part.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: chunk,
    });
    if (!putRes.ok) throw new Error(`分片 ${part.part_number} 上传失败（${putRes.status}）`);
  }

  await post('/adrive/v1.0/openFile/complete', {
    drive_id: driveId,
    file_id: created.file_id,
    upload_id: created.upload_id,
  }, token.accessToken);

  return { fileId: created.file_id, name: fileName, uploaded: true };
}

/** 列出云盘备份目录下的备份文件（按时间倒序） */
export async function listBackups(
  token: AliyunpanToken,
  driveId: string
): Promise<{ fileId: string; name: string; size: number; updatedAt: string }[]> {
  const folderId = await ensureFolder(token, driveId);
  const list = await post<{ items: FileEntry[] }>('/adrive/v1.0/openFile/list', {
    drive_id: driveId,
    parent_file_id: folderId,
    limit: 100,
  }, token.accessToken);
  return (list.items || [])
    .filter((f) => f.type === 'file' && f.name.endsWith('.json'))
    .map((f) => ({
      fileId: f.file_id,
      name: f.name,
      size: f.size || 0,
      updatedAt: f.updated_at || f.created_at || '',
    }))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export async function deleteFile(
  token: AliyunpanToken,
  driveId: string,
  fileId: string
): Promise<void> {
  await post('/adrive/v1.0/openFile/delete', { drive_id: driveId, file_id: fileId }, token.accessToken);
}

/** 保留最近 KEEP_BACKUPS 份，删除更旧的 */
export async function cleanupOldBackups(
  token: AliyunpanToken,
  driveId: string
): Promise<number> {
  const list = await listBackups(token, driveId);
  const toRemove = list.slice(KEEP_BACKUPS);
  for (const f of toRemove) {
    try {
      await deleteFile(token, driveId, f.fileId);
    } catch { /* Best-effort operation; failure is non-fatal. */ }
  }
  return toRemove.length;
}

/**
 * 解析授权回调 URL（?code=xxx&state=yyy），返回 code；非授权回调返回 null。
 * state 与发起授权时保存的值不一致（或缺失）时视为 CSRF，拒绝本次回调。
 * PKCE（S256）已在 buildAuthUrl/exchangeCode 中实现并带回退，防授权码拦截劫持。
 */
export function extractAuthCode(url: string): string | null {
  try {
    const u = new URL(url);
    const code = u.searchParams.get('code');
    if (code) {
      const expected = readStoredState();
      const got = u.searchParams.get('state');
      // 清除地址栏中的 code/state，避免刷新重复授权
      u.searchParams.delete('code');
      u.searchParams.delete('state');
      window.history.replaceState(null, '', u.toString());
      if (!expected || !got || got !== expected) {
        console.warn('阿里云盘 OAuth state 校验失败，已拒绝本次授权回调（可能为 CSRF 或非本次发起的回调）');
        throw new Error('授权回调校验失败（state 不匹配），请回到备份页重新发起授权');
      }
      return code;
    }
  } catch { /* Best-effort operation; failure is non-fatal. */ }
  return null;
}
